import { KeyboardSensor, PointerSensor, useSensor, useSensors, sortableKeyboardCoordinates } from '../adapters/dndAdapter';

/**
 * Interactive elements that should not trigger drag
 * @see https://github.com/clauderic/dnd-kit/issues/477
 */
const INTERACTIVE_ELEMENTS = ['button', 'input', 'textarea', 'select', 'option', 'a'];

/**
 * Checks if drag should be prevented for an element
 * Based on community patterns from @dnd-kit issues #477, #827
 *
 * @param {HTMLElement} element - The event target element
 * @returns {boolean} - True if drag should be prevented
 */
function shouldPreventDrag(element) {
  if (!element) return false;

  let current = element;
  while (current) {
    // 1. Check for data-no-dnd attribute (community standard pattern)
    if (current.dataset?.noDnd) {
      return true;
    }

    // 2. Check for MUI Dialog/Modal classes
    if (current.classList?.contains('MuiDialog-root') || current.classList?.contains('MuiModal-root')) {
      return true;
    }

    // 3. Check for role="dialog" or role="presentation" (accessibility)
    const role = current.getAttribute?.('role');
    if (role === 'dialog' || role === 'presentation') {
      return true;
    }

    // 4. Check for interactive elements (only at the target level)
    if (current === element && INTERACTIVE_ELEMENTS.includes(current.tagName?.toLowerCase())) {
      return true;
    }

    current = current.parentElement;
  }
  return false;
}

/**
 * Custom PointerSensor that ignores events inside dialogs/modals and interactive elements
 * This follows the recommended pattern from @dnd-kit community
 * @see https://github.com/clauderic/dnd-kit/issues/477
 * @see https://github.com/clauderic/dnd-kit/issues/827
 */
class SmartPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown',
      handler: ({ nativeEvent: event }, { onActivation }) => {
        // Prevent drag for dialogs, modals, and interactive elements
        if (shouldPreventDrag(event.target)) {
          return false;
        }

        // Default PointerSensor behavior: check for primary button
        if (event.isPrimary && event.button === 0) {
          onActivation?.({ event });
          return true;
        }
        return false;
      },
    },
  ];
}

/**
 * Hook que configura los sensores por defecto para drag and drop
 * Incluye PointerSensor para mouse/touch y KeyboardSensor para accesibilidad
 *
 * @param {Object} options - Opciones de configuración
 * @param {Object} options.pointerOptions - Opciones para PointerSensor
 * @param {Object} options.keyboardOptions - Opciones para KeyboardSensor
 * @returns {Array} Array de sensores configurados
 */
function useDndSensors(options = {}) {
  const { pointerOptions = {}, keyboardOptions = {} } = options;

  const sensors = useSensors(
    useSensor(SmartPointerSensor, {
      activationConstraint: {
        distance: 8, // Mínimo de píxeles para activar el drag
      },
      ...pointerOptions,
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      ...keyboardOptions,
    })
  );

  return sensors;
}

export default useDndSensors;
